import type { TearsheetSummary } from '@/types';

export type TearsheetListFilters = {
  timeRange?: string;
  sortBy?: string;
  userFilter?: string;
  companySearch?: string;
  currentUserId?: string | null;
};

function parseWhen(iso: string | undefined): number {
  if (!iso) return 0;
  const t = Date.parse(iso);
  return Number.isFinite(t) ? t : 0;
}

/** Match the Previous Results time-range buckets used for research lists. */
export function tearsheetInTimeRange(updatedAt: string | undefined, timeRange: string): boolean {
  if (!timeRange || timeRange === 'all') return true;
  const when = parseWhen(updatedAt);
  if (!when) return true;

  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();

  if (timeRange === 'today') return when >= startOfToday;
  if (timeRange === 'yesterday') {
    const startYesterday = startOfToday - 24 * 60 * 60 * 1000;
    return when >= startYesterday && when < startOfToday;
  }

  const days: Record<string, number> = {
    week: 7,
    month: 30,
    '3months': 90,
    '6months': 180,
    year: 365,
  };
  const windowDays = days[timeRange];
  if (!windowDays) return true;
  return when >= now.getTime() - windowDays * 24 * 60 * 60 * 1000;
}

export function filterAndSortTearsheets(
  rows: TearsheetSummary[],
  filters: TearsheetListFilters,
): TearsheetSummary[] {
  const timeRange = filters.timeRange || 'all';
  const sortBy = filters.sortBy || 'newest';
  const userFilter = filters.userFilter || 'all';
  const company = (filters.companySearch || '').trim().toLowerCase();
  const currentUserId = filters.currentUserId || null;

  let out = rows.filter((row) => {
    if (!tearsheetInTimeRange(row.updatedAt || row.createdAt, timeRange)) return false;

    if (userFilter === 'me') {
      if (!currentUserId || row.createdBy !== currentUserId) return false;
    } else if (userFilter !== 'all') {
      if (row.createdBy !== userFilter) return false;
    }

    if (company && !row.companyName.toLowerCase().includes(company)) return false;
    return true;
  });

  out = [...out].sort((a, b) => {
    const ta = parseWhen(a.updatedAt || a.createdAt);
    const tb = parseWhen(b.updatedAt || b.createdAt);
    return sortBy === 'oldest' ? ta - tb : tb - ta;
  });

  return out;
}
